/**
 * Thông báo nổi — thay cho `alert()`.
 *
 * VÌ SAO BỎ `alert()` TRONG PWA
 * Nặng hơn ở trang quản trị: `alert()` chặn luồng JavaScript, và trong app
 * này luồng đó đang chạy hàng đợi offline (`startAutoSync` mỗi 30 giây) cùng
 * bộ đếm làm mới token. Một hộp `alert` bỏ quên trên màn hình treo luôn cả
 * hai — nhân viên bỏ máy vào túi, nửa tiếng sau bản ghi vẫn chưa gửi.
 *
 * Thêm nữa, trên Chrome Android hộp `alert` hiện kèm tên miền, trông như cảnh
 * báo bảo mật của trình duyệt chứ không phải thông báo của ứng dụng.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Icon from './Icon'
import { thongDiepLoi } from '../api/loi'
import {
  NguCanhThongBao, type BoThongBao, type SacThai, type ThongBaoMoi,
} from './thong-bao-context'

interface DongThongBao extends ThongBaoMoi {
  id: number
  sac_thai: SacThai
}

const SONG_MAC_DINH: Record<SacThai, number> = {
  tot: 3500,
  tin: 4500,
  'canh-bao': 7000,
  // Lỗi KHÔNG tự tắt: nhân viên có thể đang cầm máy trong nắng và cần đọc kỹ
  // xem phải bấm gì tiếp. Tự tắt sau vài giây là mất hướng dẫn.
  loi: 0,
}

const ICON: Record<SacThai, 'check' | 'canh-bao' | 'thong-tin'> = {
  tot: 'check',
  loi: 'canh-bao',
  'canh-bao': 'canh-bao',
  tin: 'thong-tin',
}

export default function KhoThongBao({ children }: { children: React.ReactNode }) {
  const [ds, setDs] = useState<DongThongBao[]>([])
  const demRef = useRef(0)
  const dongHo = useRef(new Map<number, number>())

  const dong = useCallback((id: number) => {
    setDs((truoc) => truoc.filter((t) => t.id !== id))
    const t = dongHo.current.get(id)
    if (t) { window.clearTimeout(t); dongHo.current.delete(id) }
  }, [])

  useEffect(() => {
    const bo = dongHo.current
    return () => { bo.forEach((t) => window.clearTimeout(t)); bo.clear() }
  }, [])

  const bo: BoThongBao = useMemo(() => {
    const hien = (tb: ThongBaoMoi) => {
      const id = ++demRef.current
      const sac_thai = tb.sac_thai ?? 'tin'
      const song = tb.giay_song ?? SONG_MAC_DINH[sac_thai]
      // Giữ tối đa 3 thông báo: màn hình điện thoại hẹp, xếp chồng nhiều hơn
      // là che mất chính nội dung người dùng đang cần.
      setDs((truoc) => [...truoc.slice(-2), { ...tb, id, sac_thai }])
      if (song > 0) dongHo.current.set(id, window.setTimeout(() => dong(id), song))
    }
    return {
      hien,
      bao_loi: (loi, tieu_de = 'Chưa thực hiện được') => {
        console.error(tieu_de, loi)
        hien({ sac_thai: 'loi', tieu_de, chi_tiet: thongDiepLoi(loi) })
      },
      bao_xong: (tieu_de, chi_tiet) => hien({ sac_thai: 'tot', tieu_de, chi_tiet }),
    }
  }, [dong])

  return (
    <NguCanhThongBao.Provider value={bo}>
      {children}
      <div className="vung-thong-bao" role="status" aria-live="polite">
        {ds.map((t) => (
          <div key={t.id} className={`thong-bao ${t.sac_thai}`}>
            <div>
              <strong>
                <Icon ten={ICON[t.sac_thai]} size={15} /> {t.tieu_de}
              </strong>
              {t.chi_tiet && <p>{t.chi_tiet}</p>}
            </div>
            <button type="button" className="nut-tron-nho" onClick={() => dong(t.id)}
                    aria-label="Đóng thông báo">
              <Icon ten="dong" size={18} />
            </button>
          </div>
        ))}
      </div>
    </NguCanhThongBao.Provider>
  )
}
